/**
 * Distributor Form Component
 * Form for creating and editing distributors
 */

import React, { useState, useEffect } from 'react';
import { distributorService } from '../../services/distributorService';
import { useAutoFill } from '../../hooks/useAutoFill';
import AutoFillFieldWrapper from '../common/AutoFillFieldWrapper';
import './DistributorForm.css';

const DistributorForm = ({ distributor, onSubmit, onClose, isSubmitting, existingDistributors = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    is_active: true
  });
  const [errors, setErrors] = useState({});

  // Auto-fill functionality
  const {
    suggestions,
    isLoading: autoFillLoading,
    showSuggestions,
    currentField,
    handleFieldChange: handleAutoFillFieldChange,
    applySuggestion,
    dismissSuggestions
  } = useAutoFill(existingDistributors, {
    name: { required: true, minLength: 2 },
    contact_person: { required: true, minLength: 2 },
    email: { required: true, type: 'email' },
    phone: { required: true, type: 'phone' },
    address: { required: true, minLength: 5 },
    website: { type: 'url' }
  }, 'distributor');

  // Initialize form data when editing
  useEffect(() => {
    if (distributor) {
      setFormData({
        name: distributor.name || '',
        contact_person: distributor.contact_person || '',
        email: distributor.email || '',
        phone: distributor.phone || '',
        address: distributor.address || '',
        website: distributor.website || '',
        is_active: distributor.is_active !== false
      });
    }
  }, [distributor]);

  // Handle input changes with auto-fill
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updatedFormData = {
        ...prev,
        [field]: value
      };

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }

      // Trigger auto-fill only for text-based fields
      if (typeof value === 'string' && value.trim().length >= 2) {
        handleAutoFillFieldChange(field, value, updatedFormData, (field, value) => {
          setFormData(prevFormData => ({
            ...prevFormData,
            [field]: value
          }));
        });
      }

      return updatedFormData;
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form data
    const validation = distributorService.validateDistributorData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    // Format data for API
    const formattedData = distributorService.formatDistributorDataForAPI(formData);

    // Call onSubmit with appropriate parameters
    if (distributor) {
      onSubmit(distributor.id, formattedData);
    } else {
      onSubmit(formattedData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>{distributor ? 'Edit Distributor' : 'Create Distributor'}</h2>
          <button className="close-btn" onClick={onClose} disabled={isSubmitting}>
            ×
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="form-section">
              <h3>Basic Information</h3>
              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="name"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>Distributor Name *</label>
                    <input
                      type="text"
                      className={errors.name ? 'error' : ''}
                      placeholder="Enter distributor name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      disabled={isSubmitting}
                    />
                    {errors.name && <span className="field-error">{errors.name}</span>}
                  </div>
                </AutoFillFieldWrapper>
              </div>

              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="contact_person"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>Contact Person</label>
                    <input
                      type="text"
                      placeholder="Enter contact person name"
                      value={formData.contact_person}
                      onChange={(e) => handleInputChange('contact_person', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                </AutoFillFieldWrapper>
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-section">
              <h3>Contact Information</h3>
              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="email"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      className={errors.email ? 'error' : ''}
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      disabled={isSubmitting}
                    />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>
                </AutoFillFieldWrapper>

                <AutoFillFieldWrapper
                  fieldName="phone"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                </AutoFillFieldWrapper>
              </div>
            </div>

            {/* Additional Information */}
            <div className="form-section">
              <h3>Additional Information</h3>
              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="address"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>Address</label>
                    <textarea
                      placeholder="Enter full address"
                      rows={3}
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                </AutoFillFieldWrapper>
              </div>

              <div className="form-row">
                <AutoFillFieldWrapper
                  fieldName="website"
                  suggestions={suggestions}
                  isLoading={autoFillLoading}
                  showSuggestions={showSuggestions}
                  currentField={currentField}
                  onApplyField={(field, value) => applySuggestion(field, value, (field, value) => {
                    setFormData(prev => ({ ...prev, [field]: value }));
                  })}
                  onIgnoreField={(field) => applySuggestion(field, null, () => { })}
                >
                  <div className="form-group">
                    <label>Website</label>
                    <input
                      type="url"
                      className={errors.website ? 'error' : ''}
                      placeholder="https://www.example.com"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      disabled={isSubmitting}
                    />
                    {errors.website && <span className="field-error">{errors.website}</span>}
                  </div>
                </AutoFillFieldWrapper>
              </div>
            </div>

            {/* Status */}
            <div className="form-section">
              <h3>Status</h3>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span className="checkmark"></span>
                  Active Distributor
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : (distributor ? 'Update Distributor' : 'Create Distributor')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DistributorForm;
